import React, { useState, useEffect, useRef } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { klineMonitorService } from '../services/klineMonitorService';
import { useToastStore } from '../stores/useToastStore';
import { formatCondition } from '../services/klineTypes';
import { COMMON_TIMEFRAMES } from '../constants/timeframes';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import SelectDropdown from '../components/SelectDropdown';
import { getTimeFrameName } from '../utils/timeFrameUtils';
import { OrderSide, ComparisonOperator, ORDER_SIDE } from '@/services/enums';

interface SignalTrigger {
  id: number;
  monitorId: number;
  symbol: string;
  bar: string;
  signalType: OrderSide;
  signalDescription: string;
  conditions: Array<{
    field: string;
    operator: ComparisonOperator;
    value: number;
    met?: boolean;
  }>;
  price: number;
  indicators: any;
  triggeredAt: string;
}

const SignalTriggers: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<SignalTrigger[]>([]);
  const [pagination, setPagination] = useState<{
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  } | null>(null);
  const [pageSize] = useState(20);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [barFilter, setBarFilter] = useState('');
  const [signalTypeFilter, setSignalTypeFilter] = useState<OrderSide | ''>('');
  const setCurrentPageRoute = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);

  // 用于防止重复调用的 ref
  const loadingRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedRef = useRef(false);

  // 时间粒度选项列表（常用的时间粒度）
  const timeFrameOptions = [{ value: '', label: '全部' }, ...COMMON_TIMEFRAMES];

  const loadSignalTriggers = async (page: number = 1) => {
    // 防止重复调用
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      const response = await klineMonitorService.getSignalTriggers({
        page,
        pageSize,
        symbol: symbolFilter || undefined,
        bar: barFilter || undefined,
        signalType: signalTypeFilter || undefined,
      });
      setRecords(response.records);
      setPagination(response.pagination);
    } catch (error: any) {
      console.error('加载信号记录失败:', error);
      showToast(error.message || '加载信号记录失败', 'error');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // 初始加载
  useEffect(() => {
    // 防止重复初始化
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;
    loadSignalTriggers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 筛选条件变化时的防抖处理（不包括 symbolFilter，它需要按回车键查询）
  useEffect(() => {
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 如果已经初始化过，才执行防抖加载
    if (hasInitializedRef.current) {
      // 设置防抖定时器（500ms）
      debounceTimerRef.current = setTimeout(() => {
        loadSignalTriggers(1);
      }, 500);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barFilter, signalTypeFilter]);

  const handlePageChange = (newPage: number) => {
    if (pagination && (newPage < 1 || newPage > pagination.totalPages)) {
      return;
    }
    loadSignalTriggers(newPage);
  };

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader title="信号记录" onBack={() => setCurrentPageRoute('personal-center')} />

        {/* 筛选栏 */}
        <div
          className="fixed left-0 right-0 z-40 bg-black/10 backdrop-blur-sm border-b border-white/10 px-4 py-2 space-y-2"
          style={{ top: '56px' }}
        >
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={symbolFilter}
              onChange={(e) => setSymbolFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  loadSignalTriggers(1);
                }
              }}
              placeholder="币种筛选"
              className="w-24 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
            />
            <SelectDropdown
              value={barFilter}
              onChange={(val) => setBarFilter(val as string)}
              options={timeFrameOptions.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              className="flex-1 mb-0"
            />
            <SelectDropdown
              value={signalTypeFilter}
              onChange={(val) => setSignalTypeFilter((val || '') as OrderSide | '')}
              options={[
                { value: '', label: '全部' },
                { value: ORDER_SIDE.BUY, label: '做多' },
                { value: ORDER_SIDE.SELL, label: '做空' },
              ]}
              className="flex-1 mb-0"
            />
          </div>
        </div>

        {/* 内容区域 */}
        <div className="px-4 py-4" style={{ marginTop: '120px', paddingBottom: pagination ? '80px' : '20px' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-2">暂无信号记录</div>
            </div>
          ) : (
            <div className="space-y-3">
              {records.map((record) => (
                <div key={record.id} className="bg-white/5 border border-white/20 rounded-xl p-4 text-white/90">
                  {/* 第一行：信号类型 */}
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className={`px-3 py-1 rounded-lg text-sm font-semibold ${
                        record.signalType === ORDER_SIDE.BUY
                          ? 'bg-green-500/20 text-green-300 border border-green-500/50'
                          : 'bg-red-500/20 text-red-300 border border-red-500/50'
                      }`}
                    >
                      {record.signalType === ORDER_SIDE.BUY ? '🟢 做多' : '🔴 做空'}
                    </span>
                    <span className="text-white/60 text-xs">
                      {record.symbol} / {getTimeFrameName(record.bar)}
                    </span>
                  </div>

                  {/* 第二行：信号描述 */}
                  <div className="text-sm text-white/80 mb-3">{record.signalDescription}</div>

                  {/* 第三行：触发时的价格 */}
                  <div className="mb-3">
                    <div className="text-xs text-white/60 mb-1">触发价格</div>
                    <div className="text-lg font-semibold text-white">{record.price.toFixed(4)}</div>
                  </div>

                  {/* 第四行：条件列表 */}
                  {record.conditions && record.conditions.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs text-white/60 mb-2">触发条件</div>
                      <div className="space-y-1">
                        {record.conditions.map((condition, index) => (
                          <div key={index} className="flex items-center justify-between text-xs pl-2">
                            <div className="text-white/60">{formatCondition(condition)}</div>
                            {typeof condition.met === 'boolean' && (
                              <div className={`text-xs ${condition.met ? 'text-green-400' : 'text-red-400'}`}>
                                {condition.met ? '✓' : '✗'}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 第五行：指标信息 */}
                  {record.indicators && (
                    <div>
                      <div className="text-xs text-white/60 mb-2">技术指标</div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {record.indicators.rsi14 !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-white/60">RSI(14):</span>
                            <span className="text-white">{record.indicators.rsi14?.toFixed(2) || '-'}</span>
                          </div>
                        )}
                        {(record.indicators.macd !== undefined || record.indicators.macd?.macd !== undefined) && (
                          <div className="flex justify-between">
                            <span className="text-white/60">MACD:</span>
                            <span className="text-white">
                              {(typeof record.indicators.macd === 'object'
                                ? record.indicators.macd?.macd
                                : record.indicators.macd
                              )?.toFixed(4) || '-'}
                            </span>
                          </div>
                        )}
                        {(record.indicators.macdSignal !== undefined ||
                          record.indicators.macd?.signal !== undefined) && (
                          <div className="flex justify-between">
                            <span className="text-white/60">MACD信号:</span>
                            <span className="text-white">
                              {(typeof record.indicators.macd === 'object'
                                ? record.indicators.macd?.signal
                                : record.indicators.macdSignal
                              )?.toFixed(4) || '-'}
                            </span>
                          </div>
                        )}
                        {record.indicators.volumeRatio !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-white/60">成交量比例:</span>
                            <span className="text-white">{record.indicators.volumeRatio?.toFixed(2) || '-'}</span>
                          </div>
                        )}
                        {record.indicators.volume !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-white/60">成交量:</span>
                            <span className="text-white">{record.indicators.volume?.toFixed(2) || '-'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* 最后一行：触发时间 */}
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="text-white/40 text-xs">
                      触发时间: {new Date(record.triggeredAt).toLocaleString('zh-CN')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 分页组件 */}
        {pagination && (
          <div className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-between p-4 bg-black/20 backdrop-blur-md border-t border-white/10">
            <div className="text-sm text-white/60 whitespace-nowrap">
              共 {pagination.total} 条，第 {pagination.page} / {pagination.totalPages} 页
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={loading || pagination.page <= 1}
                className="w-10 h-10 flex items-center justify-center bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={loading || pagination.page >= pagination.totalPages}
                className="w-10 h-10 flex items-center justify-center bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </GradientBackground>
  );
};

export default SignalTriggers;
